// ============================================================================
// FSA-2.2 multi-column class table — frontend resolver (mirror of
// prima.Core/Services/FsaModule/Notes/NoteClassDataResolver.cs)
// ============================================================================
// Bridges the legacy 2-col fields and the multi-col `column_values` dict
// so every consumer (MainNotePane, drift validator, PDF preview hooks)
// reads class / line-item amounts uniformly regardless of the underlying
// schedule's layout.
//
// Backward-safe: switches to the column_values path ONLY when the
// auditor has actually populated the relevant key. Pre-Phase-2 saves
// keep rendering with their legacy amount_actual until they're re-saved
// through the multi-column UI.

import type { FormatColumnSpec } from 'api/types/fsa/template.types';

export const ROUNDED_SUFFIX = '__rounded';

export interface ColumnSpecView {
  /**
   * TRUE when the spec declares at least one column tagged `is_total`.
   * The multi-column resolver path applies only in this case. Legacy
   * 2-col specs (amount_cy / amount_py) have no is_total marker and
   * fall back to legacy fields.
   */
  isMultiColumn: boolean;
  /** Code of the column tagged `is_total`. Null when not multi-column. */
  totalCode: string | null;
  /** Code of the column tagged `is_prior_year`. Null when no prior column declared. */
  priorYearCode: string | null;
  /** All columns in the spec, in declared order. */
  columns: FormatColumnSpec[];
}

/**
 * Parse a column_spec array into a resolver-friendly view. Accepts the
 * raw array (already JSON.parsed). Returns a "not multi-column" view
 * when the spec is empty or has no is_total marker — callers fall
 * through to legacy 2-col reads.
 */
export function parseSpec(columns: FormatColumnSpec[] | null | undefined): ColumnSpecView {
  if (!columns || columns.length === 0) {
    return { isMultiColumn: false, totalCode: null, priorYearCode: null, columns: [] };
  }
  const totalCol     = columns.find(c => c.is_total === true);
  const priorYearCol = columns.find(c => c.is_prior_year === true);
  return {
    isMultiColumn: totalCol != null,
    totalCode: totalCol?.code ?? null,
    priorYearCode: priorYearCol?.code ?? null,
    columns,
  };
}

// ---------------------------------------------------------------------------
// Amount resolution — class + line item, current + prior, actual + rounded
// ---------------------------------------------------------------------------

/**
 * Shape this resolver expects. Lets us be permissive about the calling
 * site's exact NoteClassData / NoteLineItem variant (saved row, draft
 * row, hydrate response) without coupling to one DTO shape.
 */
interface HasAmounts {
  amount_actual: number;
  amount_rounded: number;
  prior_amount_actual: number;
  prior_amount_rounded: number;
  column_values?: Record<string, number> | null;
}

export function getClosingActual(cd: HasAmounts, spec: ColumnSpecView): number {
  const v = readColumn(cd.column_values, spec.totalCode);
  return v ?? cd.amount_actual;
}

export function getClosingRounded(cd: HasAmounts, spec: ColumnSpecView): number {
  const v = readColumn(cd.column_values, roundedKey(spec.totalCode));
  return v ?? cd.amount_rounded;
}

export function getOpeningActual(cd: HasAmounts, spec: ColumnSpecView): number {
  const v = readColumn(cd.column_values, spec.priorYearCode);
  return v ?? cd.prior_amount_actual;
}

export function getOpeningRounded(cd: HasAmounts, spec: ColumnSpecView): number {
  const v = readColumn(cd.column_values, roundedKey(spec.priorYearCode));
  return v ?? cd.prior_amount_rounded;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function readColumn(values: Record<string, number> | null | undefined, code: string | null): number | null {
  if (!code || !values) return null;
  const v = values[code];
  return v == null ? null : v;
}

function roundedKey(code: string | null): string | null {
  return code == null ? null : code + ROUNDED_SUFFIX;
}
